import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";
import { getPositions, addPosition } from "./PositionsSlice";
import {
  getJobTitles,
  updateJobTitle,
  deleteJobTitle,
  getJobTitleDetails,
} from "./JobTitlesSlice";
import {
  getDepartments,
  addDepartment,
  updateDepartment,
  deleteDepartment,
  getDepartmentDetails,
} from "./DepartmentsSlice";
import {
  getEmployees,
  getEmployeeDetails,
  updateEmployee,
  addEmployee,
  deleteEmployee,
  toggleEmployeeStatus,
} from "./EmployeesSlice";
import {
  getWorkShifts,
  addWorkShift,
  updateWorkShift,
  deleteWorkShift,
  getWorkShiftDetails,
} from "./WorkShifts";
import {
  getLeaveTypes,
  addLeaveType,
  updateLeaveType,
  deleteLeaveType,
  getLeaveTypeDetails,
  toggleLeaveTypeStatus,
} from "./LeaveTypesSlice";
import {
  getLeaveRequests,
  addLeaveRequest,
  updateLeaveRequest,
  deleteLeaveRequest,
  getLeaveRequestDetails,
  updateLeaveRequestStatus,
} from "./LeaveRequestsSlice";
import {
  getAttendance,
  checkIn,
  checkOut,
  getAttendanceReports,
} from "./AttendanceSlice";
import {
  getSalaries,
  addSalary,
  getSalaryDetails,
  updateSalary,
} from "./SalariesSlice";
import {
  getPayslips,
  getPayslipDetails,
  postPayslipJournal,
  getPayslipsSummary,
  getEmployeePayslipStatement,
  generatePayslip,
} from "./PayslipsSlice";
import { getLeaveReports } from "./LeaveReportsSlice";
import { getPayrollReports } from "./PayrollReportsSlice";
import { getWorkingHoursReports } from "./WorkingHoursReportsSlice";
import { getLoansReports } from "./LoansReportsSlice";
import { getPenaltiesReports } from "./PenaltiesReportsSlice";
import {
  getAccountsTree,
  getPostingAccounts,
  getAccountLinks,
  getUnlinkedAccounts,
  updateAccountLink,
  getAccountDetails,
  toggleAccountStatus,
  addAccount,
  updateAccount,
  deleteAccount,
} from "./AccountTreeSlice";
import {
  getCostCenters,
  getCostCentersTree,
  getCostCenterDetails,
  addCostCenter,
  updateCostCenter,
  toggleCostCenterStatus,
  deleteCostCenter,
} from "./CostCenterSlice";
import {
  getJournalEntries,
  getJournalEntryDetails,
  addJournalEntry,
  updateJournalEntry,
  acceptJournalEntry,
  postJournalEntry,
  deleteJournalEntry,
  reverseJournalEntry,
} from "./JournalEntriesSlice";
import {
  getReceiptVouchers,
  getReceiptVoucherDetails,
  addReceiptVoucher,
  postReceiptVoucher,
  deleteReceiptVoucher,
} from "./ReceiptVoucherSlice";
import {
  getPaymentVouchers,
  getPaymentVoucherDetails,
  addPaymentVoucher,
  editPaymentVoucher,
  postPaymentVoucher,
  deletePaymentVoucher,
} from "./PaymentVouchersSlice";
import {
  getOpeningBalances,
  addOpeningBalance,
  updateOpeningBalance,
  postOpeningBalance,
  deleteOpeningBalance,
  getOpeningBalanceDetails,
} from "./OpeningBalanceSlice";
import {
  getCurrencies,
  getCurrencyDetails,
  addCurrency,
  updateCurrency,
  toggleCurrencyStatus,
  deleteCurrency,
} from "./CurrenciesSlice";
import {
  getExchangeRates,
  getExchangeRateDetails,
  getEffectiveExchangeRate,
  addExchangeRate,
  deleteExchangeRate,
} from "./ExchangeRateSlice";
import {
  getVendors,
  getVendorDetails,
  addVendor,
  updateVendor,
  toggleVendorStatus,
  deleteVendor,
} from "./VendorSlice";
import {
  getCustomers,
  getCustomerDetails,
  addCustomer,
  updateCustomer,
  toggleCustomerStatus,
  deleteCustomer,
} from "./CustomerSlice";
import {
  getFinancialPeriods,
  getFinancialPeriodDetails,
  addFinancialPeriod,
  updateFinancialPeriod,
  closeFinancialPeriod,
  deleteFinancialPeriod,
} from "./FinancialPeriosSlice";
import { getTrialBalance } from "./TrialBalanceSlice";
import { getAccountStatement } from "./AccountsStatementSlice";
import {
  getBanks,
  getBankDetails,
  addBank,
  updateBank,
  toggleBankStatus,
  deleteBank,
} from "./BanksSlice";
import {
  getSafes,
  getSafeDetails,
  addSafe,
  updateSafe,
  toggleSafeStatus,
  deleteSafe,
} from "./SafeSlice";
import {
  getNotesReceivable,
  addNoteReceivable,
  getNoteReceivableDetails,
  collectNoteReceivable,
  cancelNoteReceivable,
  updateNoteReceivable,
} from "./NoteReceivableSlice";
import {
  getNotesPayable,
  addNotePayable,
  getNotePayableDetails,
  payNotePayable,
  cancelNotePayable,
  updateNotePayable,
} from "./NotesPayableSlice";
import {
  getTaxes,
  addTax,
  updateTax,
  getTaxDetails,
  toggleTaxStatus,
  deleteTax,
} from "./TaxSlice";
import {
  getProjects,
  addProject,
  updateProject,
  getProjectDetails,
  toggleProjectStatus,
  deleteProject,
} from "./ProjectsSlice";
import {
  getQuotations,
  addQuotation,
  updateQuotation,
  getQuotationDetails,
  sendQuotationToClient,
  approveQuotationItems,
  deleteQuotation,
} from "./QuotationSlice";
import {
  getUnits,
  getUnitDetails,
  addUnit,
  updateUnit,
  deleteUnits,
  toggleUnitStatus,
  getMeasurables,
} from "./UnitsSlice";
import {
  getLabTests,
  getLabTestsDetails,
  addLabTest,
  updateLabTest,
  deleteLabTests,
  toggleLabTestStatus,
} from "./LabTestsSlice";

// Get Banks and Safes combined

// Helper functions for Accounts Tree Optimistic Updates
const addNodeRecursive = (nodes, newAccount) => {
  if (!nodes || !Array.isArray(nodes)) return false;

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if (String(node.id) === String(newAccount.parent_id)) {
      if (!node.children) node.children = [];
      // Avoid duplicates
      if (!node.children.find(c => String(c.id) === String(newAccount.id))) {
        // Ensure newAccount has minimal required fields for JSTree if missing
        const timestamp = Date.now();
        const nodeToAdd = {
          children: [],
          ...newAccount,
          id: newAccount.id || `temp_${timestamp}`,
          // Use generic text if names are missing
          text:
            newAccount.name_ar ||
            newAccount.name_en ||
            newAccount.text ||
            "New Account",
          // Ensure names are populated for display logic
          name_ar: newAccount.name_ar || newAccount.name,
          name_en: newAccount.name_en || newAccount.name,
        };
        node.children.push(nodeToAdd);
      }
      return true;
    }
    if (node.children && node.children.length > 0) {
      if (addNodeRecursive(node.children, newAccount)) return true;
    }
  }
  return false;
};

const updateNodeRecursive = (nodes, updatedAccount) => {
  if (!nodes || !Array.isArray(nodes)) return false;
  for (let i = 0; i < nodes.length; i++) {
    if (String(nodes[i].id) === String(updatedAccount.id)) {
      nodes[i] = { ...nodes[i], ...updatedAccount };
      return true;
    }
    if (
      nodes[i].children &&
      updateNodeRecursive(nodes[i].children, updatedAccount)
    ) {
      return true;
    }
  }
  return false;
};

const deleteNodeRecursive = (nodes, accountId) => {
  if (!nodes || !Array.isArray(nodes)) return false;
  const index = nodes.findIndex(n => String(n.id) === String(accountId));
  if (index !== -1) {
    nodes.splice(index, 1);
    return true;
  }
  for (let node of nodes) {
    if (node.children && deleteNodeRecursive(node.children, accountId)) {
      return true;
    }
  }
  return false;
};

const getBanksAndSafes = createAsyncThunk(
  "auth/getBanksAndSafes",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) throw new Error("No authentication token found");
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (
        params.is_active !== undefined &&
        params.is_active !== null &&
        params.is_active !== ""
      ) {
        queryParams.append("is_active", params.is_active);
      }
      const url = `${BASE_URL}/accounts/banks-and-safes${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get banks and safes!";
      return rejectWithValue(message);
    }
  },
);

export const deleteRole = createAsyncThunk(
  "auth/deleteRole",
  async (roleId, thunkAPI) => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("useralbaraqawy"));
      let token = storedUser?.token;
      if (!token) {
        token = storedUser?.data?.token;
      }
      if (!token) {
        token = storedUser?.user?.token;
      }

      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(`${BASE_URL}/role/${roleId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "فشل في حذف الصلاحية",
      );
    }
  },
);

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (userData, { rejectWithValue }) => {
    try {
      const currentLang = getCurrentLanguage();
      const response = await axios.post(`${BASE_URL}/auth/login`, userData, {
        headers: {
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Login failed!";
      return rejectWithValue({ message });
    }
  },
);

export const registerUser = createAsyncThunk(
  "auth/registerUser",
  async (userData, { rejectWithValue }) => {
    try {
      const currentLang = getCurrentLanguage();
      const response = await axios.post(`${BASE_URL}/auth/register`, userData, {
        headers: {
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Register failed!";
      return rejectWithValue({ message });
    }
  },
);

// Get user profile
export const getUserProfile = createAsyncThunk(
  "auth/getUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to get profile!";
      return rejectWithValue({ message });
    }
  },
);

export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async (_, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/auth/logout`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to logout!";
      return rejectWithValue({ message });
    }
  },
);

// Update user profile
export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async (profileData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(`${BASE_URL}/profile`, profileData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update profile!";
      return rejectWithValue({ message });
    }
  },
);

// Change password
export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async (passwordData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/change-password`,
        passwordData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to change password!";
      return rejectWithValue({ message });
    }
  },
);

// Get roles
export const getRoles = createAsyncThunk(
  "auth/getRoles",
  async (page = 1, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/role?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to get roles!";
      return rejectWithValue({ message });
    }
  },
);

// Get permissions
export const getPermissions = createAsyncThunk(
  "auth/getPermissions",
  async (page = 1, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/permissions?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get permissions!";
      return rejectWithValue({ message });
    }
  },
);

// Create role
export const createRole = createAsyncThunk(
  "auth/createRole",
  async (roleData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(`${BASE_URL}/store_role`, roleData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to create role!";
      return rejectWithValue({ message });
    }
  },
);

// Update role
export const updateRole = createAsyncThunk(
  "auth/updateRole",
  async ({ roleId, roleData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.post(
        `${BASE_URL}/role/${roleId}`,
        roleData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update role!";
      return rejectWithValue({ message });
    }
  },
);

export const relaodFailure = createAsyncThunk(
  "auth/reloadFailure",
  async (_, { rejectWithValue }) => {
    return rejectWithValue("Session expired. Please login again.");
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: null,
    profile: null,
    roles: [],
    permissions: [],
    positions: [],
    jobTitles: [],
    jobTitleDetails: null,
    deletedJobTitleBackup: null, // For rollback on delete error
    departments: [],
    departmentDetails: null,
    deletedDepartmentBackup: null, // For rollback on delete error
    employees: [],
    employeeDetails: null,
    employeesPagination: null,
    deletedEmployeeBackup: null, // For rollback on delete error
    workShifts: [],
    workShiftDetails: null,
    deletedWorkShiftBackup: null, // For rollback on delete error
    leaveTypes: [],
    leaveTypeDetails: null,
    leaveTypesPagination: null,
    deletedLeaveTypeBackup: null, // For rollback on delete error
    toggledLeaveTypeBackup: null, // For rollback on toggle error
    leaveRequests: [],
    leaveRequestDetails: null,
    leaveRequestsPagination: null,
    deletedLeaveRequestBackup: null, // For rollback on delete error
    attendance: [],
    attendancePagination: null,
    salaries: [],
    salariesPagination: null,
    salaryDetails: null,
    payslips: [],
    payslipsPagination: null,
    payslipDetails: null,
    payslipsSummary: null,
    employeePayslipStatement: null,
    leaveReports: null,
    attendanceReports: null,
    payrollReports: null,
    workingHoursReports: null,
    loansReports: null,
    penaltiesReports: null,
    accountsTree: [],
    accountDetails: null,
    postingAccounts: [],
    costCentersTree: [],
    costCenters: [],
    costCentersPagination: null,
    costCenterDetails: null,
    journalEntries: [],
    journalEntriesPagination: null,
    deletedJournalEntryBackup: null, // For rollback on delete error
    journalEntryDetails: null,
    receiptVouchers: [],
    receiptVouchersPagination: null,
    receiptVoucherDetails: null,
    paymentVouchers: [],
    paymentVouchersPagination: null,
    paymentVoucherDetails: null,
    openingBalances: [],
    openingBalancesPagination: null,
    openingBalances: [],
    openingBalancesPagination: null,
    openingBalanceDetails: null,
    financialPeriods: [],
    financialPeriodsPagination: null,
    financialPeriodDetails: null,
    trialBalance: null,
    trialBalanceFilters: null,
    accountStatement: null,
    accountStatementTotals: null,
    accountStatementPagination: null,
    accountStatementFilters: null,
    accountStatementAccount: null,
    currencies: [],
    currenciesPagination: null,
    currencyDetails: null,
    exchangeRates: [],
    exchangeRatesPagination: null,
    exchangeRateDetails: null,
    effectiveExchangeRate: null,
    vendors: [],
    vendorsPagination: null,
    deletedVendorBackup: null, // For rollback on delete error
    toggledVendorBackup: null, // For rollback on toggle error
    vendorDetails: null,
    customers: [],
    customersPagination: null,
    deletedCustomerBackup: null, // For rollback on delete error
    toggledCustomerBackup: null, // For rollback on toggle error
    customerDetails: null,
    units: [],
    unitsPagination: null,
    unitDetails: null,
    measurables: [],
    measurablesPagination: null,
    labTests: [],
    labTestsPagination: null,
    labTestDetails: null,
    banks: [],
    banksPagination: null,
    bankDetails: null,
    safes: [],
    safesPagination: null,
    safeDetails: null,
    banksAndSafes: [],
    banksAndSafesPagination: null,
    accountLinks: null,
    unlinkedAccounts: null,
    rolesPagination: null,
    permissionsPagination: null,
    // Notes Receivable
    notesReceivable: [],
    notesReceivablePagination: null,
    noteReceivableDetails: null,
    // Notes Payable
    notesPayable: [],
    notesPayablePagination: null,
    notePayableDetails: null,
    // Taxes
    taxes: [],
    taxesPagination: null,
    taxDetails: null,
    // Projects
    projects: [],
    projectsPagination: null,
    projectDetails: null,
    quotations: [],
    quotationsPagination: null,
    deletedQuotationBackup: null, // For rollback on delete error
    quotationDetails: null,
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    hydrate: (state, action) => {
      // Hydrate state from persisted storage
      if (action.payload) {
        return { ...state, ...action.payload };
      }
      return state;
    },
    clearState: state => {
      state.error = null;
      state.success = null;
    },
    clearError: state => {
      state.error = null;
    },
    clearSuccess: state => {
      state.success = null;
    },
    clearReports: state => {
      state.leaveReports = null;
      state.attendanceReports = null;
      state.payrollReports = null;
    },
    clearAccountDetails: state => {
      state.accountDetails = null;
    },
    clearJournalEntryDetails: state => {
      state.journalEntryDetails = null;
    },
    clearExchangeRateDetails: state => {
      state.exchangeRateDetails = null;
      state.effectiveExchangeRate = null;
    },
    clearCostCenterDetails: state => {
      state.costCenterDetails = null;
    },
    clearReceiptVoucherDetails: state => {
      state.receiptVoucherDetails = null;
    },
    clearNoteReceivableDetails: state => {
      state.noteReceivableDetails = null;
    },
    clearNotePayableDetails: state => {
      state.notePayableDetails = null;
    },
    clearTaxDetails: state => {
      state.taxDetails = null;
    },
    clearPaymentVoucherDetails: state => {
      state.paymentVoucherDetails = null;
    },
    clearOpeningBalanceDetails: state => {
      state.openingBalanceDetails = null;
    },
    clearVendorDetails: state => {
      state.vendorDetails = null;
    },
    clearCustomerDetails: state => {
      state.customerDetails = null;
    },
    clearUnitDetails: state => {
      state.unitDetails = null;
    },
    clearLabTestDetails: state => {
      state.labTestDetails = null;
    },
    clearFinancialPeriodDetails: state => {
      state.financialPeriodDetails = null;
    },
    clearProjectDetails: state => {
      state.projectDetails = null;
    },
    clearQuotationDetails: state => {
      state.quotationDetails = null;
    },
    clearTrialBalance: state => {
      state.trialBalance = null;
      state.trialBalanceFilters = null;
    },
    clearAccountStatement: state => {
      state.accountStatement = null;
      state.accountStatementTotals = null;
      state.accountStatementPagination = null;
      state.accountStatementFilters = null;
    },
    clearBankDetails: state => {
      state.bankDetails = null;
    },
    clearSafeDetails: state => {
      state.safeDetails = null;
    },
    setProjects: (state, action) => {
      state.projects = action.payload;
    },
    setProjectsPagination: (state, action) => {
      state.projectsPagination = action.payload;
    },
    setCustomers: (state, action) => {
      state.customers = action.payload;
    },
    setCustomersPagination: (state, action) => {
      state.customersPagination = action.payload;
    },
    setUnits: (state, action) => {
      state.units = action.payload;
    },
    setUnitsPagination: (state, action) => {
      state.unitsPagination = action.payload;
    },
    setTaxes: (state, action) => {
      state.taxes = action.payload;
    },
    setTaxesPagination: (state, action) => {
      state.taxesPagination = action.payload;
    },
    setCostCenters: (state, action) => {
      state.costCenters = action.payload;
    },
    setCostCentersPagination: (state, action) => {
      state.costCentersPagination = action.payload;
    },
    logout: state => {
      state.user = null;
      state.error = null;
      state.success = null;
      localStorage.removeItem("useralbaraqawy");
      localStorage.removeItem("offline_prewarm_done");
    },
  },
  extraReducers: builder => {
    builder
      .addCase(loginUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;

        // حفظ البيانات المطلوبة مع التوكن
        const userDataToStore = {
          message: action.payload.message,
          data: action.payload.data,
          type: action.payload.type,
          code: action.payload.code,
          token: action.payload.data?.token,
        };

        localStorage.setItem("useralbaraqawy", JSON.stringify(userDataToStore));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Register reducers
      .addCase(registerUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
        // عند التسجيل الناجح لا نقوم بتسجيل دخول المستخدم تلقائياً
        // ولا نقوم بحفظ أي توكن في الجلسة. سيتم توجيهه لصفحة تسجيل الدخول.
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Profile reducers
      .addCase(getUserProfile.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        // البيانات تأتي مباشرة من الـ response وليس داخل data object
        state.profile = action.payload;
      })
      .addCase(getUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Update profile reducers
      .addCase(updateUserProfile.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profile = action.payload.data;
        state.success = action.payload.message;
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Change password reducers
      .addCase(changePassword.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Roles reducers
      .addCase(getRoles.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = action.payload.data;
        state.rolesPagination = action.payload.pagination;
      })
      .addCase(getRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Edit Payment Voucher
      .addCase(editPaymentVoucher.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(editPaymentVoucher.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم تعديل سند الصرف بنجاح";
        state.error = null;
      })
      .addCase(editPaymentVoucher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Permissions reducers
      .addCase(getPermissions.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload.data;
        state.permissionsPagination = action.payload.pagination;
      })
      .addCase(getPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Create role reducers
      .addCase(createRole.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(createRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Update role reducers
      .addCase(updateRole.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Delete role reducers
      .addCase(deleteRole.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Logout reducers
      .addCase(logoutUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = null;
        state.profile = null;
        state.success = null;
        state.error = null;
        localStorage.removeItem("useralbaraqawy");
        localStorage.removeItem("offline_prewarm_done");
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
        // Even if logout fails on server, clear local state
        state.user = null;
        state.profile = null;
        localStorage.removeItem("useralbaraqawy");
        localStorage.removeItem("offline_prewarm_done");
      })
      // Positions reducers
      .addCase(getPositions.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPositions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.positions = action.payload.data;
      })
      .addCase(getPositions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Add position reducers
      .addCase(addPosition.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPosition.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload.message || "تم إضافة المسمى الوظيفي بنجاح";
        state.error = null;
      })
      .addCase(addPosition.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
        state.success = null;
      })
      // Job titles reducers
      .addCase(getJobTitles.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getJobTitles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.jobTitles = action.payload.data;
      })
      .addCase(getJobTitles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Job titles additional reducers
      .addCase(updateJobTitle.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateJobTitle.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload.message || "تم تحديث المسمى الوظيفي بنجاح";
        state.error = null;
      })
      .addCase(updateJobTitle.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
        state.success = null;
      })
      .addCase(deleteJobTitle.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Optimistically remove from list
        const jobTitleId = action.meta?.arg?.id;
        if (jobTitleId !== undefined && Array.isArray(state.jobTitles)) {
          // Store backup for rollback
          const jobTitleToDelete = state.jobTitles.find(
            jt => jt.id === jobTitleId,
          );
          if (jobTitleToDelete) {
            state.deletedJobTitleBackup = jobTitleToDelete;
            state.jobTitles = state.jobTitles.filter(
              jobTitle => jobTitle.id !== jobTitleId,
            );
          }
        }
      })
      .addCase(deleteJobTitle.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = action.payload.message || "تم حذف المسمى الوظيفي بنجاح";
        state.error = null;
        // Job title was already removed optimistically in pending
        state.deletedJobTitleBackup = null;
      })
      .addCase(deleteJobTitle.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload.message;
        state.success = null;
        // Revert optimistic deletion on error
        const jobTitleId = action.meta?.arg?.id;
        const backup = state.deletedJobTitleBackup;
        if (
          jobTitleId !== undefined &&
          backup &&
          Array.isArray(state.jobTitles)
        ) {
          const exists = state.jobTitles.find(jt => jt.id === jobTitleId);
          if (!exists) {
            state.jobTitles = [...state.jobTitles, backup];
            state.jobTitles.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          }
        }
        state.deletedJobTitleBackup = null;
      })
      .addCase(getJobTitleDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getJobTitleDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.jobTitleDetails = action.payload.data || action.payload;
      })
      .addCase(getJobTitleDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload.message;
      })
      // Departments reducers
      .addCase(getDepartments.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDepartments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departments = action.payload.data;
      })
      .addCase(getDepartments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addDepartment.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addDepartment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة القسم بنجاح";
        state.error = null;
      })
      .addCase(addDepartment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(updateDepartment.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateDepartment.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث القسم بنجاح";
        state.error = null;
      })
      .addCase(updateDepartment.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(deleteDepartment.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Optimistically remove from list
        const departmentId = action.meta?.arg;
        if (departmentId !== undefined && Array.isArray(state.departments)) {
          // Store backup for rollback
          const departmentToDelete = state.departments.find(
            dept => dept.id === departmentId,
          );
          if (departmentToDelete) {
            state.deletedDepartmentBackup = departmentToDelete;
            state.departments = state.departments.filter(
              department => department.id !== departmentId,
            );
          }
        }
      })
      .addCase(deleteDepartment.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = action.payload.message || "تم حذف القسم بنجاح";
        state.error = null;
        // Department was already removed optimistically in pending
        state.deletedDepartmentBackup = null;
      })
      .addCase(deleteDepartment.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Revert optimistic deletion on error
        const departmentId = action.meta?.arg;
        const backup = state.deletedDepartmentBackup;
        if (
          departmentId !== undefined &&
          backup &&
          Array.isArray(state.departments)
        ) {
          const exists = state.departments.find(
            dept => dept.id === departmentId,
          );
          if (!exists) {
            state.departments = [...state.departments, backup];
            state.departments.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          }
        }
        state.deletedDepartmentBackup = null;
      })
      .addCase(getDepartmentDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getDepartmentDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.departmentDetails = action.payload.data;
      })
      .addCase(getDepartmentDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Contractors
      .addCase(getEmployees.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getEmployees.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employees = action.payload.data;
        state.employeesPagination = action.payload.meta;
        state.error = null;
      })
      .addCase(getEmployees.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getEmployeeDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getEmployeeDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employeeDetails = action.payload.data;
        state.error = null;
      })
      .addCase(getEmployeeDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateEmployee.pending, (state, action) => {
        // Only set loading if this is not just a status toggle
        // Check if only is_active is being updated
        const isStatusToggle = action.meta?.arg?.newIsActive !== undefined;
        if (!isStatusToggle) {
          state.isLoading = true;
        }
        state.error = null;
        // Optimistically update the employee status immediately
        const employeeId = action.meta?.arg?.id;
        const newIsActive = action.meta?.arg?.newIsActive;

        if (
          employeeId !== undefined &&
          newIsActive !== undefined &&
          Array.isArray(state.employees)
        ) {
          const index = state.employees.findIndex(emp => emp.id === employeeId);
          if (index !== -1) {
            state.employees[index] = {
              ...state.employees[index],
              is_active: newIsActive,
            };
          }
        }

        // Also update employeeDetails if it's the same employee
        if (
          employeeId !== undefined &&
          newIsActive !== undefined &&
          state.employeeDetails &&
          state.employeeDetails.id === employeeId
        ) {
          state.employeeDetails = {
            ...state.employeeDetails,
            is_active: newIsActive,
          };
        }
      })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        const isStatusToggle = action.meta?.arg?.newIsActive !== undefined;
        if (!isStatusToggle) {
          state.isLoading = false;
        }
        // Only show success message if not a status toggle (to avoid toast spam)
        if (!isStatusToggle) {
          state.success = action.payload.message || "تم تحديث الموظف بنجاح";
        }
        state.error = null;
        // The state was already updated optimistically in pending
        // Just verify with API response if available
        const employeeId = action.meta?.arg?.id;
        const updatedEmployee =
          action.payload?.data || action.payload?.employee || action.payload;

        // If API returns updated employee data, merge it (but keep is_active from optimistic update)
        if (
          updatedEmployee &&
          updatedEmployee.id &&
          Array.isArray(state.employees)
        ) {
          const index = state.employees.findIndex(
            emp => emp.id === updatedEmployee.id,
          );
          if (index !== -1) {
            const currentIsActive = state.employees[index].is_active;
            state.employees[index] = {
              ...state.employees[index],
              ...updatedEmployee,
            };
            // Preserve the optimistic update if newIsActive was provided
            const newIsActive = action.meta?.arg?.newIsActive;
            if (newIsActive !== undefined) {
              state.employees[index].is_active = newIsActive;
            }
          }
        }

        // Also update employeeDetails if it's the same employee
        if (
          updatedEmployee &&
          updatedEmployee.id &&
          state.employeeDetails &&
          state.employeeDetails.id === updatedEmployee.id
        ) {
          const currentIsActive = state.employeeDetails.is_active;
          state.employeeDetails = {
            ...state.employeeDetails,
            ...updatedEmployee,
          };
          const newIsActive = action.meta?.arg?.newIsActive;
          if (newIsActive !== undefined) {
            state.employeeDetails.is_active = newIsActive;
          }
        }
      })
      .addCase(updateEmployee.rejected, (state, action) => {
        const isStatusToggle = action.meta?.arg?.newIsActive !== undefined;
        if (!isStatusToggle) {
          state.isLoading = false;
        }
        state.error = action.payload;
        state.success = null;
        // Revert optimistic update on error
        const employeeId = action.meta?.arg?.id;
        const originalIsActive = action.meta?.arg?.originalIsActive;

        if (
          employeeId !== undefined &&
          originalIsActive !== undefined &&
          Array.isArray(state.employees)
        ) {
          const index = state.employees.findIndex(emp => emp.id === employeeId);
          if (index !== -1) {
            state.employees[index] = {
              ...state.employees[index],
              is_active: originalIsActive,
            };
          }
        }

        // Also revert employeeDetails if it's the same employee
        if (
          employeeId !== undefined &&
          originalIsActive !== undefined &&
          state.employeeDetails &&
          state.employeeDetails.id === employeeId
        ) {
          state.employeeDetails = {
            ...state.employeeDetails,
            is_active: originalIsActive,
          };
        }
      })
      .addCase(addEmployee.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addEmployee.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة الموظف بنجاح";
        state.error = null;
      })
      .addCase(addEmployee.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(deleteEmployee.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Optimistically remove employee from the list immediately
        const employeeId = action.meta?.arg;
        if (employeeId !== undefined && Array.isArray(state.employees)) {
          // Store the employee for potential rollback
          const employeeToDelete = state.employees.find(
            emp => emp.id === employeeId,
          );
          if (employeeToDelete) {
            // Store backup for rollback
            state.deletedEmployeeBackup = {
              employee: employeeToDelete,
              employeeDetails:
                state.employeeDetails && state.employeeDetails.id === employeeId
                  ? state.employeeDetails
                  : null,
            };
            // Remove from list immediately
            state.employees = state.employees.filter(
              emp => emp.id !== employeeId,
            );
          }
        }
        // Also clear employeeDetails if it's the same employee
        if (
          employeeId !== undefined &&
          state.employeeDetails &&
          state.employeeDetails.id === employeeId
        ) {
          if (!state.deletedEmployeeBackup) {
            state.deletedEmployeeBackup = {
              employee: null,
              employeeDetails: state.employeeDetails,
            };
          } else {
            state.deletedEmployeeBackup.employeeDetails = state.employeeDetails;
          }
          state.employeeDetails = null;
        }
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = action.payload.message || "تم حذف الموظف بنجاح";
        state.error = null;
        // Employee was already removed optimistically in pending
        // Clear the backup
        state.deletedEmployeeBackup = null;
      })
      .addCase(deleteEmployee.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Revert optimistic deletion on error
        const employeeId = action.meta?.arg;
        const backup = state.deletedEmployeeBackup;

        if (
          employeeId !== undefined &&
          backup &&
          backup.employee &&
          Array.isArray(state.employees)
        ) {
          // Check if employee was actually removed
          const exists = state.employees.find(emp => emp.id === employeeId);
          if (!exists) {
            // Restore the employee to the list
            state.employees = [...state.employees, backup.employee];
            // Sort to maintain order if needed (by id if available)
            state.employees.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          }
        }

        // Restore employeeDetails if it was cleared
        if (backup && backup.employeeDetails && !state.employeeDetails) {
          state.employeeDetails = backup.employeeDetails;
        }

        // Clear the backup
        state.deletedEmployeeBackup = null;
      })
      .addCase(toggleEmployeeStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleEmployeeStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم تغيير الحالة بنجاح";
        state.error = null;
        // Update employee status in the list
        const employeeId = action.meta.arg;
        const employee = state.employees.find(emp => emp.id === employeeId);
        if (employee) {
          employee.is_active = !employee.is_active;
        }
      })
      .addCase(toggleEmployeeStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(getWorkShifts.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getWorkShifts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workShifts = action.payload.data;
        state.error = null;
      })
      .addCase(getWorkShifts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addWorkShift.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addWorkShift.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة الوردية بنجاح";
        state.error = null;
        if (action.payload.data) {
          state.workShifts.push(action.payload.data);
        }
      })
      .addCase(addWorkShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(updateWorkShift.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateWorkShift.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث الوردية بنجاح";
        state.error = null;
        if (action.payload.data) {
          const index = state.workShifts.findIndex(
            shift => shift.id === action.meta.arg.id,
          );
          if (index !== -1) {
            state.workShifts[index] = action.payload.data;
          }
        }
      })
      .addCase(updateWorkShift.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(deleteWorkShift.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Optimistically remove from list
        const workShiftId = action.meta?.arg;
        if (workShiftId !== undefined && Array.isArray(state.workShifts)) {
          // Store backup for rollback
          const workShiftToDelete = state.workShifts.find(
            ws => ws.id === workShiftId,
          );
          if (workShiftToDelete) {
            state.deletedWorkShiftBackup = workShiftToDelete;
            state.workShifts = state.workShifts.filter(
              shift => shift.id !== workShiftId,
            );
          }
        }
      })
      .addCase(deleteWorkShift.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = action.payload.message || "تم حذف الوردية بنجاح";
        state.error = null;
        // Work shift was already removed optimistically in pending
        state.deletedWorkShiftBackup = null;
      })
      .addCase(deleteWorkShift.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Revert optimistic deletion on error
        const workShiftId = action.meta?.arg;
        const backup = state.deletedWorkShiftBackup;
        if (
          workShiftId !== undefined &&
          backup &&
          Array.isArray(state.workShifts)
        ) {
          const exists = state.workShifts.find(ws => ws.id === workShiftId);
          if (!exists) {
            state.workShifts = [...state.workShifts, backup];
            state.workShifts.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          }
        }
        state.deletedWorkShiftBackup = null;
      })
      .addCase(getWorkShiftDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getWorkShiftDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workShiftDetails = action.payload.data;
        state.error = null;
      })
      .addCase(getWorkShiftDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Leave types reducers
      .addCase(getLeaveTypes.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLeaveTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leaveTypes = action.payload.data;
        state.leaveTypesPagination = action.payload.meta;
        state.error = null;
      })
      .addCase(getLeaveTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addLeaveType.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addLeaveType.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة نوع الإجازة بنجاح";
        state.error = null;
        if (action.payload.data) {
          state.leaveTypes.push(action.payload.data);
        }
      })
      .addCase(addLeaveType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(updateLeaveType.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateLeaveType.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث نوع الإجازة بنجاح";
        state.error = null;
        if (action.payload.data) {
          const index = state.leaveTypes.findIndex(
            leaveType => leaveType.id === action.meta.arg.id,
          );
          if (index !== -1) {
            state.leaveTypes[index] = action.payload.data;
          }
        }
      })
      .addCase(updateLeaveType.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(deleteLeaveType.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Optimistically remove from list
        const leaveTypeId = action.meta?.arg;
        if (leaveTypeId !== undefined && Array.isArray(state.leaveTypes)) {
          // Store backup for rollback
          const leaveTypeToDelete = state.leaveTypes.find(
            lt => lt.id === leaveTypeId,
          );
          if (leaveTypeToDelete) {
            state.deletedLeaveTypeBackup = leaveTypeToDelete;
            state.leaveTypes = state.leaveTypes.filter(
              leaveType => leaveType.id !== leaveTypeId,
            );
          }
        }
      })
      .addCase(deleteLeaveType.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = action.payload.message || "تم حذف نوع الإجازة بنجاح";
        state.error = null;
        // Leave type was already removed optimistically in pending
        state.deletedLeaveTypeBackup = null;
      })
      .addCase(deleteLeaveType.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Revert optimistic deletion on error
        const leaveTypeId = action.meta?.arg;
        const backup = state.deletedLeaveTypeBackup;
        if (
          leaveTypeId !== undefined &&
          backup &&
          Array.isArray(state.leaveTypes)
        ) {
          const exists = state.leaveTypes.find(lt => lt.id === leaveTypeId);
          if (!exists) {
            state.leaveTypes = [...state.leaveTypes, backup];
            state.leaveTypes.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          }
        }
        state.deletedLeaveTypeBackup = null;
      })
      .addCase(toggleLeaveTypeStatus.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Optimistically update the leave type status immediately
        const leaveTypeId = action.meta?.arg;
        if (leaveTypeId !== undefined && Array.isArray(state.leaveTypes)) {
          const index = state.leaveTypes.findIndex(lt => lt.id === leaveTypeId);
          if (index !== -1) {
            // Store original state for rollback
            state.toggledLeaveTypeBackup = {
              id: leaveTypeId,
              originalIsActive: state.leaveTypes[index].is_active,
            };
            state.leaveTypes[index] = {
              ...state.leaveTypes[index],
              is_active: !state.leaveTypes[index].is_active,
            };
          }
        }
      })
      .addCase(toggleLeaveTypeStatus.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        // Only show success message if not a status toggle (to avoid toast spam)
        state.success = null;
        state.error = null;
        // The state was already updated optimistically in pending
        // Just verify with API response if available
        const updatedLeaveType = action.payload?.data;
        if (
          updatedLeaveType &&
          updatedLeaveType.id &&
          Array.isArray(state.leaveTypes)
        ) {
          const index = state.leaveTypes.findIndex(
            lt => lt.id === updatedLeaveType.id,
          );
          if (index !== -1) {
            // Keep the optimistic update (is_active was already toggled)
            state.leaveTypes[index] = {
              ...state.leaveTypes[index],
              ...updatedLeaveType,
              // Preserve the toggled status
              is_active: state.leaveTypes[index].is_active,
            };
          }
        }
        state.toggledLeaveTypeBackup = null;
      })
      .addCase(toggleLeaveTypeStatus.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Revert optimistic update on error
        const backup = state.toggledLeaveTypeBackup;
        if (
          backup &&
          backup.id !== undefined &&
          Array.isArray(state.leaveTypes)
        ) {
          const index = state.leaveTypes.findIndex(lt => lt.id === backup.id);
          if (index !== -1) {
            state.leaveTypes[index] = {
              ...state.leaveTypes[index],
              is_active: backup.originalIsActive,
            };
          }
        }
        state.toggledLeaveTypeBackup = null;
      })
      .addCase(getLeaveTypeDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLeaveTypeDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leaveTypeDetails = action.payload.data;
        state.error = null;
      })
      .addCase(getLeaveTypeDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Leave requests reducers
      .addCase(getLeaveRequests.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLeaveRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leaveRequests = action.payload.data;
        state.leaveRequestsPagination = action.payload.meta;
        state.error = null;
      })
      .addCase(getLeaveRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addLeaveRequest.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addLeaveRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة طلب الإجازة بنجاح";
        state.error = null;
        if (action.payload.data) {
          state.leaveRequests.push(action.payload.data);
        }
      })
      .addCase(addLeaveRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(updateLeaveRequest.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateLeaveRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث طلب الإجازة بنجاح";
        state.error = null;
        if (action.payload.data) {
          const index = state.leaveRequests.findIndex(
            request => request.id === action.meta.arg.id,
          );
          if (index !== -1) {
            state.leaveRequests[index] = action.payload.data;
          }
        }
      })
      .addCase(updateLeaveRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(deleteLeaveRequest.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Only optimistically remove if status is pending (can be deleted)
        const leaveRequestId = action.meta?.arg;
        if (
          leaveRequestId !== undefined &&
          Array.isArray(state.leaveRequests)
        ) {
          const leaveRequestToDelete = state.leaveRequests.find(
            lr => lr.id === leaveRequestId,
          );
          // Only do optimistic delete if status is pending
          if (
            leaveRequestToDelete &&
            leaveRequestToDelete.status === "pending"
          ) {
            state.deletedLeaveRequestBackup = leaveRequestToDelete;
            state.leaveRequests = state.leaveRequests.filter(
              request => request.id !== leaveRequestId,
            );
          }
        }
      })
      .addCase(deleteLeaveRequest.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = action.payload.message || "تم حذف طلب الإجازة بنجاح";
        state.error = null;
        // Leave request was already removed optimistically in pending
        state.deletedLeaveRequestBackup = null;
      })
      .addCase(deleteLeaveRequest.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Revert optimistic deletion on error
        const leaveRequestId = action.meta?.arg;
        const backup = state.deletedLeaveRequestBackup;
        if (
          leaveRequestId !== undefined &&
          backup &&
          Array.isArray(state.leaveRequests)
        ) {
          const exists = state.leaveRequests.find(
            lr => lr.id === leaveRequestId,
          );
          if (!exists) {
            state.leaveRequests = [...state.leaveRequests, backup];
            state.leaveRequests.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          }
        }
        state.deletedLeaveRequestBackup = null;
      })
      .addCase(getLeaveRequestDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLeaveRequestDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leaveRequestDetails = action.payload.data;
        state.error = null;
      })
      .addCase(getLeaveRequestDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateLeaveRequestStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateLeaveRequestStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload.message || "تم تحديث حالة طلب الإجازة بنجاح";
        state.error = null;
        if (action.payload.data) {
          const index = state.leaveRequests.findIndex(
            request => request.id === action.meta.arg.id,
          );
          if (index !== -1) {
            state.leaveRequests[index] = action.payload.data;
          }
        }
      })
      .addCase(updateLeaveRequestStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      // Attendance reducers
      .addCase(getAttendance.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAttendance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.attendance = action.payload.data;
        state.attendancePagination = action.payload.meta;
        state.error = null;
      })
      .addCase(getAttendance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(checkIn.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkIn.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تسجيل الحضور بنجاح";
        state.error = null;
      })
      .addCase(checkIn.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(checkOut.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkOut.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تسجيل الانصراف بنجاح";
        state.error = null;
      })
      .addCase(checkOut.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      // Salaries reducers
      .addCase(getSalaries.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSalaries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.salaries = action.payload.data;
        state.salariesPagination = action.payload.meta;
        state.error = null;
      })
      .addCase(getSalaries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addSalary.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addSalary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة الراتب بنجاح";
        state.error = null;
      })
      .addCase(addSalary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      .addCase(getSalaryDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSalaryDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.salaryDetails = action.payload.data;
        state.error = null;
      })
      .addCase(getSalaryDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateSalary.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSalary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث الراتب بنجاح";
        state.error = null;
      })
      .addCase(updateSalary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.success = null;
      })
      // Get payslips
      .addCase(getPayslips.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPayslips.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payslips = action.payload.data;
        state.payslipsPagination = action.payload.meta;
        state.error = null;
      })
      .addCase(getPayslips.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get payslip details
      .addCase(getPayslipDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPayslipDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payslipDetails = action.payload.data;
        state.error = null;
      })
      .addCase(getPayslipDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Post payslip journal
      .addCase(postPayslipJournal.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(postPayslipJournal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم ترحيل كشف الراتب بنجاح!";
        state.error = null;
      })
      .addCase(postPayslipJournal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get payslips summary
      .addCase(getPayslipsSummary.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPayslipsSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payslipsSummary = action.payload.data;
        state.error = null;
      })
      .addCase(getPayslipsSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get employee payslip statement
      .addCase(getEmployeePayslipStatement.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getEmployeePayslipStatement.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employeePayslipStatement = action.payload.data;
        state.error = null;
      })
      .addCase(getEmployeePayslipStatement.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Generate payslip
      .addCase(generatePayslip.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(generatePayslip.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم توليد كشف الراتب بنجاح!";
        state.error = null;
      })
      .addCase(generatePayslip.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get leave reports
      .addCase(getLeaveReports.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLeaveReports.fulfilled, (state, action) => {
        state.isLoading = false;
        state.leaveReports = action.payload;
        state.error = null;
      })
      .addCase(getLeaveReports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get attendance reports
      .addCase(getAttendanceReports.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAttendanceReports.fulfilled, (state, action) => {
        state.isLoading = false;
        state.attendanceReports = action.payload;
        state.error = null;
      })
      .addCase(getAttendanceReports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get payroll reports
      .addCase(getPayrollReports.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPayrollReports.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payrollReports = action.payload;
        state.error = null;
      })
      .addCase(getPayrollReports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get working hours reports
      .addCase(getWorkingHoursReports.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getWorkingHoursReports.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workingHoursReports = action.payload;
        state.error = null;
      })
      .addCase(getWorkingHoursReports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get loans reports
      .addCase(getLoansReports.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLoansReports.fulfilled, (state, action) => {
        state.isLoading = false;
        state.loansReports = action.payload;
        state.error = null;
      })
      .addCase(getLoansReports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get penalties reports
      .addCase(getPenaltiesReports.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPenaltiesReports.fulfilled, (state, action) => {
        state.isLoading = false;
        state.penaltiesReports = action.payload;
        state.error = null;
      })
      .addCase(getPenaltiesReports.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get accounts tree
      .addCase(getAccountsTree.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAccountsTree.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accountsTree = action.payload.data || [];
        state.error = null;
      })
      .addCase(getAccountsTree.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Posting Accounts
      .addCase(getPostingAccounts.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPostingAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle various API response shapes: array, {data: []}, {data: {data: []}}
        const payload = action.payload;
        if (Array.isArray(payload)) {
          state.postingAccounts = payload;
        } else if (Array.isArray(payload?.data?.data)) {
          state.postingAccounts = payload.data.data;
        } else if (Array.isArray(payload?.data)) {
          state.postingAccounts = payload.data;
        } else {
          state.postingAccounts = [];
        }
        state.error = null;
      })
      .addCase(getPostingAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Account Links
      .addCase(getAccountLinks.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAccountLinks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accountLinks = action.payload;
        state.error = null;
      })
      .addCase(getAccountLinks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Unlinked Accounts
      .addCase(getUnlinkedAccounts.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUnlinkedAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.unlinkedAccounts = action.payload;
        state.error = null;
      })
      .addCase(getUnlinkedAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Account Link
      .addCase(updateAccountLink.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAccountLink.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(updateAccountLink.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get account details
      .addCase(getAccountDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAccountDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accountDetails = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(getAccountDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Toggle account status
      .addCase(toggleAccountStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleAccountStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تغيير حالة الحساب بنجاح";
        state.error = null;
      })
      .addCase(toggleAccountStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add account
      .addCase(addAccount.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة الحساب بنجاح";
        state.error = null;

        // Optimistic Update for Offline/Online
        let newAccount = action.payload.data || action.payload; // payload might be data directly or {data: ...}

        // If data is missing (e.g. offline FormData request not fully parsed yet), try to construct it from args
        if (
          !newAccount ||
          (!newAccount.id && !newAccount.name_ar && !newAccount.name_en)
        ) {
          const formData = action.meta.arg;
          if (formData instanceof FormData) {
            newAccount = {};
            formData.forEach((value, key) => {
              newAccount[key] = value;
            });
          } else {
            newAccount = { ...formData };
          }
        }

        if (newAccount) {
          // Ensure ID
          if (!newAccount.id) {
            newAccount.id = `temp_${Date.now()}`;
          }

          const accountToAdd = { ...newAccount };

          // If parent is root (null, 0, '#')
          if (
            !accountToAdd.parent_id ||
            accountToAdd.parent_id === "0" ||
            accountToAdd.parent_id === 0 ||
            accountToAdd.parent_id === "#"
          ) {
            state.accountsTree = state.accountsTree || [];
            // Avoid duplicates
            if (
              !state.accountsTree.find(
                n => String(n.id) === String(accountToAdd.id),
              )
            ) {
              state.accountsTree.push({
                children: [],
                ...accountToAdd,
                // Use generic text if names are missing
                text:
                  accountToAdd.name_ar ||
                  accountToAdd.name_en ||
                  accountToAdd.text ||
                  "New Account",
                // Ensure names are populated for display logic
                name_ar: accountToAdd.name_ar || accountToAdd.name,
                name_en: accountToAdd.name_en || accountToAdd.name,
              });
            }
          } else {
            // Try to add to parent
            // Ensure helpers are available or inline the logic if safer (we confirmed they exist)
            addNodeRecursive(state.accountsTree, accountToAdd);
          }
        }
      })
      .addCase(addAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update account
      .addCase(updateAccount.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث الحساب بنجاح";
        state.error = null;

        // Optimistic Update
        let updatedAccount = action.payload.data || action.payload;

        // Ensure ID is present (it might be in meta.arg but not in the payload/formData)
        if (
          (!updatedAccount || !updatedAccount.id) &&
          action.meta?.arg?.accountId
        ) {
          updatedAccount = { ...updatedAccount, id: action.meta.arg.accountId };
        }

        if (updatedAccount && updatedAccount.id) {
          updateNodeRecursive(state.accountsTree, updatedAccount);
          // Also update details if currently viewing this account
          if (
            state.accountDetails &&
            String(state.accountDetails.id) === String(updatedAccount.id)
          ) {
            state.accountDetails = {
              ...state.accountDetails,
              ...updatedAccount,
            };
          }
        }
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete account
      .addCase(deleteAccount.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم حذف الحساب بنجاح";
        state.error = null;

        // Optimistic Update
        const accountId = action.meta.arg;
        if (accountId) {
          deleteNodeRecursive(state.accountsTree, accountId);
          // Clear details if viewing deleted account
          if (
            state.accountDetails &&
            String(state.accountDetails.id) === String(accountId)
          ) {
            state.accountDetails = null;
          }
        }
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Cost Centers
      .addCase(getCostCenters.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCostCenters.fulfilled, (state, action) => {
        state.isLoading = false;
        state.costCenters =
          action.payload.data?.data || action.payload.data || [];
        state.costCentersPagination =
          action.payload.data?.meta || action.payload.meta || null;
        state.error = null;
      })
      .addCase(getCostCenters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getCostCentersTree.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCostCentersTree.fulfilled, (state, action) => {
        state.isLoading = false;
        state.costCentersTree = action.payload.data || [];
        state.error = null;
      })
      .addCase(getCostCentersTree.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getCostCenterDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCostCenterDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.costCenterDetails = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(getCostCenterDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addCostCenter.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addCostCenter.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة مركز التكلفة";
        state.error = null;

        // Optimistic Update
        let newCenter = action.payload.data || action.payload;
        if (
          !newCenter ||
          (!newCenter.id && !newCenter.name_ar && !newCenter.name_en)
        ) {
          const formData = action.meta.arg;
          if (formData instanceof FormData) {
            newCenter = {};
            formData.forEach((value, key) => {
              newCenter[key] = value;
            });
          } else {
            newCenter = { ...formData };
          }
        }

        if (newCenter) {
          if (!newCenter.id) newCenter.id = `temp_${Date.now()}`;
          if (!newCenter.children) newCenter.children = [];

          // Populate default text/names if missing for display
          newCenter.text =
            newCenter.name_ar ||
            newCenter.name_en ||
            newCenter.text ||
            "New Cost Center";
          newCenter.name_ar = newCenter.name_ar || newCenter.name;
          newCenter.name_en = newCenter.name_en || newCenter.name;

          const parentId = newCenter.parent_id;
          if (
            !parentId ||
            parentId === "0" ||
            parentId === 0 ||
            parentId === "#"
          ) {
            state.costCentersTree = state.costCentersTree || [];
            if (
              !state.costCentersTree.find(
                n => String(n.id) === String(newCenter.id),
              )
            ) {
              state.costCentersTree.push(newCenter);
            }
          } else {
            // Reuse addNodeRecursive from Accounts (it works on generic children structure)
            addNodeRecursive(state.costCentersTree, newCenter);
          }
        }
      })
      .addCase(addCostCenter.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(updateCostCenter.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCostCenter.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تعديل مركز التكلفة";
        state.error = null;

        // Optimistic Update
        let updatedCenter = action.payload.data || action.payload;
        if ((!updatedCenter || !updatedCenter.id) && action.meta?.arg?.id) {
          // If payload missing ID, try to construct minimum update from args
          // Assuming arg is the update object or FormData with ID
          const argV = action.meta.arg;
          if (argV instanceof FormData) {
            updatedCenter = { id: argV.get("id") };
            argV.forEach((v, k) => (updatedCenter[k] = v));
          } else {
            updatedCenter = { ...argV };
          }
        }

        if (updatedCenter && updatedCenter.id) {
          updateNodeRecursive(state.costCentersTree, updatedCenter);
          // Update details if viewing
          if (state.costCenterDetails) {
            const detailsId =
              state.costCenterDetails.data?.id || state.costCenterDetails.id;
            if (String(detailsId) === String(updatedCenter.id)) {
              state.costCenterDetails = {
                ...state.costCenterDetails,
                ...updatedCenter,
                data: {
                  ...(state.costCenterDetails.data || {}),
                  ...updatedCenter,
                },
              };
            }
          }
        }
      })
      .addCase(updateCostCenter.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(toggleCostCenterStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleCostCenterStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تغيير حالة مركز التكلفة";
        state.error = null;

        // Optimistic Toggle
        const centerId = action.meta.arg;
        if (centerId) {
          // Helper to toggle active status in tree?
          // Since updateNodeRecursive merges props, we can just call it with { id, is_active: !currentStatus }
          // But we need to know current status. easier to find node and toggle.
          // Or just rely on re-fetch if lazy?
          // Better: Traverse and toggle.
          const toggleRecursive = nodes => {
            for (let node of nodes) {
              if (String(node.id) === String(centerId)) {
                node.is_active = !node.is_active;
                return true;
              }
              if (node.children && toggleRecursive(node.children)) return true;
            }
            return false;
          };
          if (state.costCentersTree) toggleRecursive(state.costCentersTree);

          if (state.costCenterDetails) {
            const detailsId =
              state.costCenterDetails.data?.id || state.costCenterDetails.id;
            if (String(detailsId) === String(centerId)) {
              const isActive = state.costCenterDetails.data
                ? state.costCenterDetails.data.is_active
                : state.costCenterDetails.is_active;
              const newState = !isActive;
              if (state.costCenterDetails.data)
                state.costCenterDetails.data.is_active = newState;
              else state.costCenterDetails.is_active = newState;
            }
          }
        }
      })
      .addCase(toggleCostCenterStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      .addCase(deleteCostCenter.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCostCenter.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم حذف مركز التكلفة";
        state.error = null;

        // Optimistic Update
        const centerId = action.meta.arg;
        if (centerId) {
          deleteNodeRecursive(state.costCentersTree, centerId);
          if (state.costCenterDetails) {
            const detailsId =
              state.costCenterDetails.data?.id || state.costCenterDetails.id;
            if (String(detailsId) === String(centerId)) {
              state.costCenterDetails = null;
            }
          }
        }
      })
      .addCase(deleteCostCenter.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Journal Entries
      .addCase(getJournalEntries.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getJournalEntries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.journalEntries = action.payload.data || [];
        state.journalEntriesPagination = action.payload.meta || null;
        state.error = null;
      })
      .addCase(getJournalEntries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Journal Entry Details
      .addCase(getJournalEntryDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getJournalEntryDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.journalEntryDetails = action.payload.data || null;
        state.error = null;
      })
      .addCase(getJournalEntryDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Journal Entry
      .addCase(addJournalEntry.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addJournalEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة القيد بنجاح";
        state.error = null;

        // Optimistic Update
        let newData = action.payload.data || action.meta.arg;
        if (newData) {
          if (Array.isArray(state.journalEntries)) {
            if (newData instanceof FormData) {
              if (
                action.payload.data &&
                !(action.payload.data instanceof FormData)
              ) {
                newData = action.payload.data;
              } else {
                return;
              }
            }

            const itemToAdd = { ...newData };
            if (!itemToAdd.id) {
              itemToAdd.id = `temp_${Date.now()}`;
            }

            const exists = state.journalEntries.some(
              e => e.id === itemToAdd.id,
            );
            if (!exists) {
              state.journalEntries.unshift(itemToAdd);
            }
          }
        }
      })
      .addCase(addJournalEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Journal Entry
      .addCase(updateJournalEntry.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateJournalEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث القيد بنجاح";
        state.error = null;

        // Optimistic Update
        const updatedData = action.payload.data || action.meta.arg.entryData;
        const entryId = action.payload.id || action.meta.arg.entryId;

        if (entryId && Array.isArray(state.journalEntries)) {
          const index = state.journalEntries.findIndex(e => e.id == entryId);
          if (index !== -1) {
            state.journalEntries[index] = {
              ...state.journalEntries[index],
              ...updatedData,
            };
          }
        }
      })
      .addCase(updateJournalEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Accept Journal Entry
      .addCase(acceptJournalEntry.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(acceptJournalEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم قبول القيد بنجاح";
        state.error = null;
      })
      .addCase(acceptJournalEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Post Journal Entry
      .addCase(postJournalEntry.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(postJournalEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم ترحيل القيد بنجاح";
        state.error = null;
      })
      .addCase(postJournalEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Journal Entry
      .addCase(deleteJournalEntry.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Don't do optimistic delete - we can't know if entry is linked to another system
        // The component will handle the UI update after successful deletion
      })
      .addCase(deleteJournalEntry.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = action.payload.message || "تم حذف القيد بنجاح";
        state.error = null;
        // Remove from list after successful deletion
        const entryId = action.meta?.arg;
        if (entryId !== undefined && Array.isArray(state.journalEntries)) {
          state.journalEntries = state.journalEntries.filter(
            entry => entry.id !== entryId,
          );
        }
      })
      .addCase(deleteJournalEntry.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Entry remains in list on error
      })
      // Reverse Journal Entry
      .addCase(reverseJournalEntry.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(reverseJournalEntry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم عكس القيد بنجاح";
        state.error = null;
      })
      .addCase(reverseJournalEntry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Receipt Vouchers
      .addCase(getReceiptVouchers.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getReceiptVouchers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.receiptVouchers =
          action.payload.data?.data || action.payload.data || [];
        state.receiptVouchersPagination =
          action.payload.data?.meta || action.payload.meta || null;
        state.error = null;
      })
      .addCase(getReceiptVouchers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getReceiptVoucherDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getReceiptVoucherDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.receiptVoucherDetails = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(getReceiptVoucherDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addReceiptVoucher.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addReceiptVoucher.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload.message || "تم إضافة إيصال الاستلام بنجاح";
        state.error = null;

        // Optimistic Update
        let newData = action.payload.data || action.meta.arg;
        if (newData) {
          // Handle FormData interaction if needed
          // Ensure we have an object to work with
          if (newData instanceof FormData) {
            // Try to extract from payload.data (optimistic mock) first as it is JSON
            if (
              action.payload.data &&
              !(action.payload.data instanceof FormData)
            ) {
              newData = action.payload.data;
            } else {
              // Fallback: we cannot easily optimistically add FormData without parsing
              // But usually offline manager provides parsed data in payload.data
              // If we are here, newData is still FormData, which we shouldn't push to state
              return;
            }
          }

          // Create a copy to avoid mutating action payload
          const itemToAdd = { ...newData };

          // Generate temp ID if missing (for offline items)
          if (!itemToAdd.id) {
            itemToAdd.id = `temp_${Date.now()}`;
          }

          if (Array.isArray(state.receiptVouchers)) {
            const exists = state.receiptVouchers.some(
              v => v.id === itemToAdd.id,
            );
            if (!exists) {
              state.receiptVouchers.unshift(itemToAdd);
            }
          }
        }
      })
      .addCase(addReceiptVoucher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(postReceiptVoucher.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(postReceiptVoucher.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload.message || "تم ترحيل إيصال الاستلام بنجاح";
        state.error = null;
      })
      .addCase(postReceiptVoucher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteReceiptVoucher.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Don't do optimistic delete - component handles it
      })
      .addCase(deleteReceiptVoucher.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = action.payload.message || "تم حذف إيصال الاستلام بنجاح";
        state.error = null;
        // Remove from list after successful deletion
        const voucherId = action.meta?.arg;
        if (voucherId !== undefined && Array.isArray(state.receiptVouchers)) {
          state.receiptVouchers = state.receiptVouchers.filter(
            voucher => voucher.id !== voucherId,
          );
        }
      })
      .addCase(deleteReceiptVoucher.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Voucher remains in list on error
      })
      // Payment Vouchers
      .addCase(getPaymentVouchers.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPaymentVouchers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paymentVouchers =
          action.payload.data?.data || action.payload.data || [];
        state.paymentVouchersPagination =
          action.payload.data?.meta || action.payload.meta || null;
        state.error = null;
      })
      .addCase(getPaymentVouchers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getPaymentVoucherDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPaymentVoucherDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.paymentVoucherDetails = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(getPaymentVoucherDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addPaymentVoucher.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addPaymentVoucher.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة سند الصرف بنجاح";
        state.error = null;

        // Optimistic Update
        let newData = action.payload.data || action.meta.arg;
        if (newData) {
          if (Array.isArray(state.paymentVouchers)) {
            if (newData instanceof FormData) {
              if (
                action.payload.data &&
                !(action.payload.data instanceof FormData)
              ) {
                newData = action.payload.data;
              } else {
                return;
              }
            }

            const itemToAdd = { ...newData };
            if (!itemToAdd.id) {
              itemToAdd.id = `temp_${Date.now()}`;
            }

            const exists = state.paymentVouchers.some(
              e => e.id === itemToAdd.id,
            );
            if (!exists) {
              state.paymentVouchers.unshift(itemToAdd);
            }
          }
        }
      })
      .addCase(addPaymentVoucher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(postPaymentVoucher.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(postPaymentVoucher.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload.message || "تم ترحيل إيصال صرف نقدية بنجاح";
        state.error = null;
      })
      .addCase(postPaymentVoucher.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deletePaymentVoucher.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Don't do optimistic delete - component handles it
      })
      .addCase(deletePaymentVoucher.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success =
          action.payload.message || "تم حذف إيصال صرف نقدية بنجاح";
        state.error = null;
        // Remove from list after successful deletion
        const voucherId = action.meta?.arg;
        if (voucherId !== undefined && Array.isArray(state.paymentVouchers)) {
          state.paymentVouchers = state.paymentVouchers.filter(
            voucher => voucher.id !== voucherId,
          );
        }
      })
      .addCase(deletePaymentVoucher.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Voucher remains in list on error
      })
      // Opening Balances
      .addCase(getOpeningBalances.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOpeningBalances.fulfilled, (state, action) => {
        state.isLoading = false;
        state.openingBalances =
          action.payload.data?.data || action.payload.data || [];
        state.openingBalancesPagination =
          action.payload.data?.meta || action.payload.meta || null;
        state.error = null;
      })
      .addCase(getOpeningBalances.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addOpeningBalance.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addOpeningBalance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload.message || "تم إضافة الرصيد الافتتاحي بنجاح";
        state.error = null;

        // Optimistic Update
        let newData = action.payload.data || action.meta.arg;
        if (newData) {
          if (Array.isArray(state.openingBalances)) {
            // Ensure strictly unique ID if possibly duplicated by retry
            // If newData is FormData, we need to handle it or skip (usually handled by offline manager returning object)
            if (newData instanceof FormData) {
              if (
                action.payload.data &&
                !(action.payload.data instanceof FormData)
              ) {
                newData = action.payload.data;
              } else {
                return;
              }
            }

            const itemToAdd = { ...newData };
            if (!itemToAdd.id) {
              itemToAdd.id = `temp_${Date.now()}`;
            }

            const exists = state.openingBalances.some(
              item => item.id === itemToAdd.id,
            );
            if (!exists) {
              state.openingBalances.unshift(itemToAdd);
            }
          }
        }
      })
      .addCase(addOpeningBalance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateOpeningBalance.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOpeningBalance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload.message || "تم تحديث الرصيد الافتتاحي بنجاح";
        state.error = null;

        // Optimistic Update
        const updatedData = action.payload.data || action.meta.arg.data;
        const id = action.payload.id || action.meta.arg.id;

        if (id && Array.isArray(state.openingBalances)) {
          const index = state.openingBalances.findIndex(
            item => item.id == id || item.entry?.id == id,
          );
          if (index !== -1) {
            // If the list item structure is nested (e.g. has entry property), preserve structure
            if (state.openingBalances[index].entry) {
              state.openingBalances[index].entry = {
                ...state.openingBalances[index].entry,
                ...updatedData,
              };
            } else {
              state.openingBalances[index] = {
                ...state.openingBalances[index],
                ...updatedData,
              };
            }
          }
        }
      })
      .addCase(updateOpeningBalance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(postOpeningBalance.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(postOpeningBalance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload.message || "تم ترحيل الرصيد الافتتاحي بنجاح";
        state.error = null;
      })
      .addCase(postOpeningBalance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteOpeningBalance.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteOpeningBalance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload.message || "تم حذف الرصيد الافتتاحي بنجاح";
        state.error = null;

        // Optimistic Update
        const id = action.meta.arg;
        if (id && Array.isArray(state.openingBalances)) {
          state.openingBalances = state.openingBalances.filter(
            item => item.id != id && item.entry?.id != id,
          );
        }
      })
      .addCase(deleteOpeningBalance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getOpeningBalanceDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.openingBalanceDetails = null;
      })
      .addCase(getOpeningBalanceDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.openingBalanceDetails =
          action.payload.data || action.payload.entry || action.payload;
        state.error = null;
      })
      .addCase(getOpeningBalanceDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Currencies
      .addCase(getCurrencies.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCurrencies.fulfilled, (state, action) => {
        state.isLoading = false;

        // Preserve optimistic (temp) items that are not yet synced
        const currentItems = state.currencies || [];

        const payloadData = action.payload?.data;
        const list = Array.isArray(payloadData?.data)
          ? payloadData.data
          : Array.isArray(payloadData)
            ? payloadData
            : Array.isArray(action.payload)
              ? action.payload
              : [];

        const meta = payloadData?.meta || action.payload?.meta || null;

        // Robust merge: keep existing items and update/add new ones
        const merged = [...currentItems];
        list.forEach(newItem => {
          const index = merged.findIndex(
            i => String(i.id) === String(newItem.id),
          );
          if (index !== -1) {
            merged[index] = { ...merged[index], ...newItem };
          } else {
            merged.push(newItem);
          }
        });

        state.currencies = merged;
        state.currenciesPagination = meta
          ? {
              current_page: meta.current_page || 1,
              last_page: meta.last_page || 1,
              per_page: meta.per_page || list.length || 10,
              total: meta.total || list.length || 0,
            }
          : null;
        state.error = null;
      })
      .addCase(getCurrencies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Currency Details
      .addCase(getCurrencyDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCurrencyDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currencyDetails = action.payload.data || action.payload;
        state.error = null;
      })
      .addCase(getCurrencyDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Currency
      .addCase(addCurrency.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addCurrency.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة العملة بنجاح";
        state.error = null;

        // Optimistic Update
        let newData = action.payload.data || action.meta.arg;
        if (newData) {
          if (Array.isArray(state.currencies)) {
            if (newData instanceof FormData) {
              if (
                action.payload.data &&
                !(action.payload.data instanceof FormData)
              ) {
                newData = action.payload.data;
              } else {
                return;
              }
            }

            const itemToAdd = { ...newData };
            if (!itemToAdd.id) {
              itemToAdd.id = `temp_${Date.now()}`;
            }

            const exists = state.currencies.some(e => e.id === itemToAdd.id);
            if (!exists) {
              state.currencies.unshift(itemToAdd);
            }
          }
        }
      })
      .addCase(addCurrency.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Currency
      .addCase(updateCurrency.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCurrency.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث العملة بنجاح";
        state.error = null;

        // Optimistic Update
        const updatedData = action.payload.data || action.meta.arg.data;
        const id = action.payload.id || action.meta.arg.id;

        if (id && Array.isArray(state.currencies)) {
          const index = state.currencies.findIndex(item => item.id == id);
          if (index !== -1) {
            state.currencies[index] = {
              ...state.currencies[index],
              ...updatedData,
            };
          }
          // Also update details if currently viewing this currency
          if (
            state.currencyDetails &&
            String(state.currencyDetails.id) === String(id)
          ) {
            state.currencyDetails = {
              ...state.currencyDetails,
              ...updatedData,
            };
          }
        }
      })
      .addCase(updateCurrency.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Toggle Currency Status
      .addCase(toggleCurrencyStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleCurrencyStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تغيير حالة العملة بنجاح";
        state.error = null;
        // Optimistic Update for Toggle
        const id = action.meta.arg;
        if (id && Array.isArray(state.currencies)) {
          const index = state.currencies.findIndex(item => item.id == id);
          if (index !== -1) {
            state.currencies[index].is_active =
              !state.currencies[index].is_active;
          }
        }
      })
      .addCase(toggleCurrencyStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Currency
      .addCase(deleteCurrency.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCurrency.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم حذف العملة بنجاح";
        state.error = null;

        // Optimistic Update
        const id = action.meta.arg;
        if (id && Array.isArray(state.currencies)) {
          state.currencies = state.currencies.filter(item => item.id != id);
        }
      })
      .addCase(deleteCurrency.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Exchange Rates
      .addCase(getExchangeRates.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getExchangeRates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.exchangeRates = action.payload.data || [];
        state.exchangeRatesPagination = {
          current_page: action.payload.meta?.current_page || 1,
          last_page: action.payload.meta?.last_page || 1,
          per_page: action.payload.meta?.per_page || 10,
          total: action.payload.meta?.total || 0,
          links: action.payload.links || {},
        };
        state.error = null;
      })
      .addCase(getExchangeRates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Exchange Rate Details
      .addCase(getExchangeRateDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getExchangeRateDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.exchangeRateDetails = action.payload.data || null;
        state.error = null;
      })
      .addCase(getExchangeRateDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Effective Exchange Rate
      .addCase(getEffectiveExchangeRate.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getEffectiveExchangeRate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.effectiveExchangeRate = action.payload.data || null;
        state.error = null;
      })
      .addCase(getEffectiveExchangeRate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Exchange Rate
      .addCase(addExchangeRate.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addExchangeRate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة سعر الصرف بنجاح";
        state.error = null;
      })
      .addCase(addExchangeRate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Exchange Rate
      .addCase(deleteExchangeRate.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteExchangeRate.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم حذف سعر الصرف بنجاح";
        state.error = null;
      })
      .addCase(deleteExchangeRate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Vendors
      .addCase(getVendors.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getVendors.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vendors = action.payload.data || [];
        state.vendorsPagination = {
          current_page: action.payload.meta?.current_page || 1,
          last_page: action.payload.meta?.last_page || 1,
          per_page: action.payload.meta?.per_page || 10,
          total: action.payload.meta?.total || 0,
          links: action.payload.links || {},
        };
        state.error = null;
      })
      .addCase(getVendors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Vendor Details
      .addCase(getVendorDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getVendorDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vendorDetails = action.payload.data || action.payload || null;
        state.error = null;
      })
      .addCase(getVendorDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Vendor
      .addCase(addVendor.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addVendor.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة المورد بنجاح";
        state.error = null;
      })
      .addCase(addVendor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Vendor
      .addCase(updateVendor.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateVendor.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث المورد بنجاح";
        state.error = null;
      })
      .addCase(updateVendor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Toggle Vendor Status
      .addCase(toggleVendorStatus.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Optimistically update the vendor status immediately
        const arg = action.meta?.arg;
        const vendorId = typeof arg === "object" && arg !== null ? arg.id : arg;
        const newIsActive =
          typeof arg === "object" && arg !== null ? arg.newIsActive : undefined;

        if (
          vendorId !== undefined &&
          newIsActive !== undefined &&
          Array.isArray(state.vendors)
        ) {
          const index = state.vendors.findIndex(v => v.id === vendorId);
          if (index !== -1) {
            // Store original state for rollback
            state.toggledVendorBackup = {
              id: vendorId,
              originalIsActive: state.vendors[index].is_active,
            };
            state.vendors[index] = {
              ...state.vendors[index],
              is_active: newIsActive,
            };
          }
        }
      })
      .addCase(toggleVendorStatus.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        // Only show success message if not a status toggle (to avoid toast spam)
        state.success = null;
        state.error = null;
        // The state was already updated optimistically in pending
        // Just verify with API response if available
        const updatedVendor = action.payload?.data;
        if (updatedVendor && updatedVendor.id && Array.isArray(state.vendors)) {
          const index = state.vendors.findIndex(v => v.id === updatedVendor.id);
          if (index !== -1) {
            // Keep the optimistic update (is_active was already toggled)
            state.vendors[index] = {
              ...state.vendors[index],
              ...updatedVendor,
              // Preserve the toggled status
              is_active: state.vendors[index].is_active,
            };
          }
        }
        state.toggledVendorBackup = null;
      })
      .addCase(toggleVendorStatus.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Revert optimistic update on error
        const backup = state.toggledVendorBackup;
        if (backup && backup.id !== undefined && Array.isArray(state.vendors)) {
          const index = state.vendors.findIndex(v => v.id === backup.id);
          if (index !== -1) {
            state.vendors[index] = {
              ...state.vendors[index],
              is_active: backup.originalIsActive,
            };
          }
        }
        state.toggledVendorBackup = null;
      })
      // Delete Vendor
      .addCase(deleteVendor.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Optimistically remove from list
        const vendorId = action.meta?.arg;
        if (vendorId !== undefined && Array.isArray(state.vendors)) {
          // Store backup for rollback
          const vendorToDelete = state.vendors.find(v => v.id === vendorId);
          if (vendorToDelete) {
            state.deletedVendorBackup = vendorToDelete;
            state.vendors = state.vendors.filter(
              vendor => vendor.id !== vendorId,
            );
          }
        }
      })
      .addCase(deleteVendor.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = action.payload.message || "تم حذف المورد بنجاح";
        state.error = null;
        // Vendor was already removed optimistically in pending
        state.deletedVendorBackup = null;
      })
      .addCase(deleteVendor.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Revert optimistic deletion on error
        const vendorId = action.meta?.arg;
        const backup = state.deletedVendorBackup;
        if (vendorId !== undefined && backup && Array.isArray(state.vendors)) {
          const exists = state.vendors.find(v => v.id === vendorId);
          if (!exists) {
            state.vendors = [...state.vendors, backup];
            state.vendors.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          }
        }
        state.deletedVendorBackup = null;
      })
      // Get Customers
      .addCase(getCustomers.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = action.payload.data?.data || [];
        state.customersPagination = {
          current_page: action.payload.data?.meta?.current_page || 1,
          last_page: action.payload.data?.meta?.last_page || 1,
          per_page: action.payload.data?.meta?.per_page || 10,
          total: action.payload.data?.meta?.total || 0,
        };
        state.error = null;
      })
      .addCase(getCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Customer Details
      .addCase(getCustomerDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getCustomerDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customerDetails = action.payload.data || action.payload || null;
        state.error = null;
      })
      .addCase(getCustomerDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Customer
      .addCase(addCustomer.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة العميل بنجاح";
        state.error = null;
      })
      .addCase(addCustomer.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload;
        if (typeof payload === "string") {
          state.error = payload;
        } else if (payload?.message) {
          state.error = payload.message;
        } else if (payload?.errors) {
          state.error = Object.values(payload.errors).flat().join(", ");
        } else {
          state.error = "فشل إضافة العميل";
        }
      })
      // Update Customer
      .addCase(updateCustomer.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث العميل بنجاح";
        state.error = null;
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload;
        if (typeof payload === "string") {
          state.error = payload;
        } else if (payload?.message) {
          state.error = payload.message;
        } else if (payload?.errors) {
          state.error = Object.values(payload.errors).flat().join(", ");
        } else {
          state.error = "فشل تحديث العميل";
        }
      })
      // Toggle Customer Status
      .addCase(toggleCustomerStatus.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Optimistically update the customer status immediately
        const arg = action.meta?.arg;
        const customerId =
          typeof arg === "object" && arg !== null ? arg.id : arg;
        const newIsActive =
          typeof arg === "object" && arg !== null ? arg.newIsActive : undefined;

        if (
          customerId !== undefined &&
          newIsActive !== undefined &&
          Array.isArray(state.customers)
        ) {
          const index = state.customers.findIndex(c => c.id === customerId);
          if (index !== -1) {
            // Store original state for rollback
            state.toggledCustomerBackup = {
              id: customerId,
              originalIsActive: state.customers[index].is_active,
            };
            state.customers[index] = {
              ...state.customers[index],
              is_active: newIsActive,
            };
          }
        }
      })
      .addCase(toggleCustomerStatus.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = null; // Suppress success message to avoid toast spam
        state.error = null;
        // The state was already updated optimistically in pending
        state.toggledCustomerBackup = null;
      })
      .addCase(toggleCustomerStatus.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Revert optimistic update on error
        const backup = state.toggledCustomerBackup;
        if (
          backup &&
          backup.id !== undefined &&
          Array.isArray(state.customers)
        ) {
          const index = state.customers.findIndex(c => c.id === backup.id);
          if (index !== -1) {
            state.customers[index] = {
              ...state.customers[index],
              is_active: backup.originalIsActive,
            };
          }
        }
        state.toggledCustomerBackup = null;
      })
      // Delete Customer
      .addCase(deleteCustomer.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Optimistically remove from list
        const customerId = action.meta?.arg;
        if (customerId !== undefined && Array.isArray(state.customers)) {
          // Store backup for rollback
          const customerToDelete = state.customers.find(
            c => c.id === customerId,
          );
          if (customerToDelete) {
            state.deletedCustomerBackup = customerToDelete;
            state.customers = state.customers.filter(
              customer => customer.id !== customerId,
            );
          }
        }
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = action.payload.message || "تم حذف العميل بنجاح";
        state.error = null;
        // Customer was already removed optimistically in pending
        state.deletedCustomerBackup = null;
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Revert optimistic deletion on error
        const customerId = action.meta?.arg;
        const backup = state.deletedCustomerBackup;
        if (
          customerId !== undefined &&
          backup &&
          Array.isArray(state.customers)
        ) {
          const exists = state.customers.find(c => c.id === customerId);
          if (!exists) {
            state.customers = [...state.customers, backup];
            state.customers.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          }
        }
        state.deletedCustomerBackup = null;
      })
      // Financial Periods
      .addCase(getFinancialPeriods.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getFinancialPeriods.fulfilled, (state, action) => {
        state.isLoading = false;
        const payloadData = action.payload?.data;
        const list = Array.isArray(payloadData?.data)
          ? payloadData.data
          : Array.isArray(payloadData)
            ? payloadData
            : [];
        const meta = payloadData?.meta || action.payload?.meta || null;
        state.financialPeriods = list;
        state.financialPeriodsPagination = meta
          ? {
              current_page: meta.current_page || 1,
              last_page: meta.last_page || 1,
              per_page: meta.per_page || list.length || 10,
              total: meta.total || list.length || 0,
            }
          : null;
        state.error = null;
      })
      .addCase(getFinancialPeriods.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getFinancialPeriodDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getFinancialPeriodDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.financialPeriodDetails =
          action.payload?.data || action.payload || null;
        state.error = null;
      })
      .addCase(getFinancialPeriodDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addFinancialPeriod.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addFinancialPeriod.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message || "تم إضافة الفترة المالية بنجاح";
        state.error = null;
      })
      .addCase(addFinancialPeriod.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateFinancialPeriod.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateFinancialPeriod.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message || "تم تحديث الفترة المالية بنجاح";
        state.error = null;
      })
      .addCase(updateFinancialPeriod.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(closeFinancialPeriod.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(closeFinancialPeriod.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message || "تم إقفال الفترة المالية بنجاح";
        state.error = null;
      })
      .addCase(closeFinancialPeriod.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteFinancialPeriod.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Don't do optimistic delete - component handles it
      })
      .addCase(deleteFinancialPeriod.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success =
          action.payload?.message || "تم حذف الفترة المالية بنجاح";
        state.error = null;
        // Don't remove from Redux state here - component handles it after successful deletion
        // This prevents the item from disappearing and reappearing on error
      })
      .addCase(deleteFinancialPeriod.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Period remains in list on error
      })
      .addCase(getTrialBalance.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTrialBalance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.trialBalance =
          action.payload?.data?.data || action.payload?.data || null;
        state.trialBalanceFilters = action.payload?.params || null;
        state.error = null;
      })
      .addCase(getTrialBalance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getAccountStatement.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAccountStatement.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload =
          action.payload?.data?.data || action.payload?.data || null;
        // Support both old structure (movements) and new structure (transactions)
        state.accountStatement =
          payload?.transactions ||
          payload?.movements?.data ||
          payload?.movements ||
          [];
        state.accountStatementTotals =
          payload?.summary || payload?.totals || null;
        state.accountStatementPagination = payload?.movements || null;
        state.accountStatementFilters = action.payload?.params || null;
        state.accountStatementAccount = payload?.account || null; // Store account info
        state.success = action.payload?.data?.message || null;
        state.error = null;
      })
      .addCase(getAccountStatement.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Banks
      .addCase(getBanks.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBanks.fulfilled, (state, action) => {
        state.isLoading = false;
        state.banks = action.payload.data?.data || [];
        state.banksPagination = {
          current_page: action.payload.data?.meta?.current_page || 1,
          last_page: action.payload.data?.meta?.last_page || 1,
          per_page: action.payload.data?.meta?.per_page || 10,
          total: action.payload.data?.meta?.total || 0,
        };
        state.error = null;
      })
      .addCase(getBanks.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Bank Details
      .addCase(getBankDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBankDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.bankDetails = action.payload.data || action.payload || null;
        state.error = null;
      })
      .addCase(getBankDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Bank
      .addCase(addBank.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addBank.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة البنك بنجاح";
        state.error = null;
      })
      .addCase(addBank.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Bank
      .addCase(updateBank.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateBank.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث البنك بنجاح";
        state.error = null;
      })
      .addCase(updateBank.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Toggle Bank Status
      .addCase(toggleBankStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleBankStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تغيير حالة البنك بنجاح";
        state.error = null;
      })
      .addCase(toggleBankStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Bank
      .addCase(deleteBank.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteBank.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم حذف البنك بنجاح";
        state.error = null;
      })
      .addCase(deleteBank.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Units
      .addCase(getUnits.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUnits.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload?.data || action.payload || {};
        const items = Array.isArray(payload)
          ? payload
          : payload.data || payload.units || [];
        state.units = Array.isArray(items) ? items : [];
        const meta = payload.meta || action.payload?.data?.meta || null;
        state.unitsPagination = meta
          ? {
              current_page: meta.current_page || 1,
              last_page: meta.last_page || 1,
              per_page: meta.per_page || 10,
              total: meta.total || (Array.isArray(items) ? items.length : 0),
            }
          : null;
        state.error = null;
      })
      .addCase(getUnits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getUnitDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUnitDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.unitDetails = action.payload?.data || action.payload || null;
        state.error = null;
      })
      .addCase(getUnitDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addUnit.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addUnit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || null;
        state.error = null;
      })
      .addCase(addUnit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUnit.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUnit.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || null;
        state.error = null;
      })
      .addCase(updateUnit.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteUnits.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteUnits.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || null;
        state.error = null;
      })
      .addCase(deleteUnits.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleUnitStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleUnitStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || null;
        state.error = null;
      })
      .addCase(toggleUnitStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Measurables
      .addCase(getMeasurables.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMeasurables.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload?.data || action.payload || {};
        const items = Array.isArray(payload)
          ? payload
          : payload.data || payload.measurables || [];
        state.measurables = Array.isArray(items) ? items : [];
        const meta = payload.meta || action.payload?.data?.meta || null;
        state.measurablesPagination = meta
          ? {
              current_page: meta.current_page || 1,
              last_page: meta.last_page || 1,
              per_page: meta.per_page || 10,
              total: meta.total || (Array.isArray(items) ? items.length : 0),
            }
          : null;
        state.error = null;
      })
      .addCase(getMeasurables.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Lab Tests
      .addCase(getLabTests.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLabTests.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload?.data || action.payload || {};
        const items = Array.isArray(payload)
          ? payload
          : payload.data || payload.labTests || [];
        state.labTests = Array.isArray(items) ? items : [];
        const meta = payload.meta || action.payload?.data?.meta || null;
        state.labTestsPagination = meta
          ? {
              current_page: meta.current_page || 1,
              last_page: meta.last_page || 1,
              per_page: meta.per_page || 10,
              total: meta.total || (Array.isArray(items) ? items.length : 0),
            }
          : null;
        state.error = null;
      })
      .addCase(getLabTests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getLabTestsDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getLabTestsDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.labTestDetails = action.payload?.data || action.payload || null;
        state.error = null;
      })
      .addCase(getLabTestsDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addLabTest.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addLabTest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || null;
        state.error = null;
      })
      .addCase(addLabTest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateLabTest.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateLabTest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || null;
        state.error = null;
      })
      .addCase(updateLabTest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteLabTests.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteLabTests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || null;
        state.error = null;
      })
      .addCase(deleteLabTests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleLabTestStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleLabTestStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || null;
        state.error = null;
      })
      .addCase(toggleLabTestStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Safes
      .addCase(getSafes.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSafes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.safes = action.payload.data?.data || [];
        state.safesPagination = {
          current_page: action.payload.data?.meta?.current_page || 1,
          last_page: action.payload.data?.meta?.last_page || 1,
          per_page: action.payload.data?.meta?.per_page || 10,
          total: action.payload.data?.meta?.total || 0,
        };
        state.error = null;
      })
      .addCase(getSafes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Safe Details
      .addCase(getSafeDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSafeDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.safeDetails = action.payload.data || action.payload || null;
        state.error = null;
      })
      .addCase(getSafeDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Banks and Safes
      .addCase(getBanksAndSafes.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getBanksAndSafes.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload?.data || action.payload;

        // Extract children from banks and safes
        const banksChildren = payload?.banks?.children || [];
        const safesChildren = payload?.safes?.children || [];

        // Combine banks and safes children into a single array
        state.banksAndSafes = [...banksChildren, ...safesChildren];

        state.banksAndSafesPagination = {
          current_page: 1,
          last_page: 1,
          per_page: state.banksAndSafes.length,
          total: state.banksAndSafes.length,
        };
        state.error = null;
      })
      .addCase(getBanksAndSafes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Safe
      .addCase(addSafe.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addSafe.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم إضافة الخزنة بنجاح";
        state.error = null;
      })
      .addCase(addSafe.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Safe
      .addCase(updateSafe.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateSafe.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تحديث الخزنة بنجاح";
        state.error = null;
      })
      .addCase(updateSafe.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Toggle Safe Status
      .addCase(toggleSafeStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleSafeStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم تغيير حالة الخزنة بنجاح";
        state.error = null;
      })
      .addCase(toggleSafeStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Notes Receivable
      .addCase(getNotesReceivable.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getNotesReceivable.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload?.data;
        state.notesReceivable =
          payload?.data?.data || payload?.data || payload || [];
        state.notesReceivablePagination = {
          current_page: payload?.meta?.current_page || 1,
          last_page: payload?.meta?.last_page || 1,
          per_page: payload?.meta?.per_page || 10,
          total:
            payload?.meta?.total ||
            (Array.isArray(state.notesReceivable)
              ? state.notesReceivable.length
              : 0),
        };
        state.error = null;
      })
      .addCase(getNotesReceivable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addNoteReceivable.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addNoteReceivable.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم إضافة ورقة القبض بنجاح";
        state.error = null;
      })
      .addCase(addNoteReceivable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getNoteReceivableDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getNoteReceivableDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.noteReceivableDetails =
          action.payload?.data || action.payload || null;
        state.error = null;
      })
      .addCase(getNoteReceivableDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(collectNoteReceivable.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(collectNoteReceivable.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم تحصيل ورقة القبض بنجاح";
        state.error = null;
      })
      .addCase(collectNoteReceivable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(cancelNoteReceivable.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelNoteReceivable.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم إلغاء التحصيل بنجاح";
        state.error = null;
      })
      .addCase(cancelNoteReceivable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateNoteReceivable.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateNoteReceivable.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم تحديث ورقة القبض بنجاح";
        state.error = null;
      })
      .addCase(updateNoteReceivable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Notes Payable
      .addCase(getNotesPayable.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getNotesPayable.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload?.data;
        state.notesPayable =
          payload?.data?.data || payload?.data || payload || [];
        state.notesPayablePagination = {
          current_page: payload?.meta?.current_page || 1,
          last_page: payload?.meta?.last_page || 1,
          per_page: payload?.meta?.per_page || 10,
          total:
            payload?.meta?.total ||
            (Array.isArray(state.notesPayable) ? state.notesPayable.length : 0),
        };
        state.error = null;
      })
      .addCase(getNotesPayable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addNotePayable.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addNotePayable.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم إضافة ورقة الدفع بنجاح";
        state.error = null;
      })
      .addCase(addNotePayable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getNotePayableDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getNotePayableDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notePayableDetails =
          action.payload?.data || action.payload || null;
        state.error = null;
      })
      .addCase(getNotePayableDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(payNotePayable.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(payNotePayable.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم دفع ورقة الدفع بنجاح";
        state.error = null;
      })
      .addCase(payNotePayable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(cancelNotePayable.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cancelNotePayable.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم إلغاء الدفع بنجاح";
        state.error = null;
      })
      .addCase(cancelNotePayable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateNotePayable.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateNotePayable.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم تحديث ورقة الدفع بنجاح";
        state.error = null;
      })
      .addCase(updateNotePayable.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Taxes
      .addCase(getTaxes.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTaxes.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload?.data;
        state.taxes = payload?.data?.data || payload?.data || payload || [];
        state.taxesPagination = {
          current_page: payload?.meta?.current_page || 1,
          last_page: payload?.meta?.last_page || 1,
          per_page: payload?.meta?.per_page || 10,
          total:
            payload?.meta?.total ||
            (Array.isArray(state.taxes) ? state.taxes.length : 0),
        };
        state.error = null;
      })
      .addCase(getTaxes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addTax.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addTax.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم إضافة الضريبة بنجاح";
        state.error = null;
      })
      .addCase(addTax.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateTax.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateTax.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم تعديل الضريبة بنجاح";
        state.error = null;
      })
      .addCase(updateTax.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getTaxDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTaxDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.taxDetails = action.payload?.data || action.payload || null;
        state.error = null;
      })
      .addCase(getTaxDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleTaxStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleTaxStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message || "تم تحديث حالة الضريبة بنجاح";
        state.error = null;
      })
      .addCase(toggleTaxStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteTax.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteTax.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم حذف الضريبة بنجاح";
        state.error = null;
      })
      .addCase(deleteTax.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Projects
      .addCase(getProjects.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProjects.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload?.data;
        const list = Array.isArray(payload?.data?.data)
          ? payload.data.data
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
        const meta =
          payload?.meta || payload?.data?.meta || action.payload?.meta || null;
        state.projects = list;
        state.projectsPagination = {
          current_page: meta?.current_page || 1,
          last_page: meta?.last_page || 1,
          per_page: meta?.per_page || list.length || 10,
          total: meta?.total || list.length || 0,
        };
        state.error = null;
      })
      .addCase(getProjects.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addProject.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addProject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم إضافة المشروع بنجاح";
        state.error = null;
      })
      .addCase(addProject.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateProject.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم تحديث المشروع بنجاح";
        state.error = null;
      })
      .addCase(updateProject.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getProjectDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProjectDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.projectDetails = action.payload?.data || action.payload || null;
        state.error = null;
      })
      .addCase(getProjectDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleProjectStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleProjectStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message || "تم تحديث حالة المشروع بنجاح";
        state.error = null;
      })
      .addCase(toggleProjectStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteProject.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم حذف المشروع بنجاح";
        state.error = null;
      })
      .addCase(deleteProject.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Quotations
      .addCase(getQuotations.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getQuotations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotations =
          action.payload?.data?.data || action.payload?.data || [];
        state.quotationsPagination =
          action.payload?.data?.meta || action.payload?.meta || null;
        state.error = null;
      })
      .addCase(getQuotations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addQuotation.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addQuotation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم إضافة عرض السعر بنجاح";
        state.error = null;
      })
      .addCase(addQuotation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateQuotation.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateQuotation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "تم تحديث عرض السعر بنجاح";
        state.error = null;
      })
      .addCase(updateQuotation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getQuotationDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getQuotationDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotationDetails = action.payload?.data || action.payload || null;
        state.error = null;
      })
      .addCase(getQuotationDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(sendQuotationToClient.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(sendQuotationToClient.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message || "تم إرسال عرض السعر للعميل بنجاح";
        state.error = null;
      })
      .addCase(sendQuotationToClient.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(approveQuotationItems.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(approveQuotationItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message || "تم اعتماد بنود عرض السعر بنجاح";
        state.error = null;
      })
      .addCase(approveQuotationItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteQuotation.pending, (state, action) => {
        // Don't set isLoading to avoid page reload
        state.error = null;
        // Optimistically remove from list
        const quotationId = action.meta?.arg;
        if (quotationId !== undefined && Array.isArray(state.quotations)) {
          // Store backup for rollback
          const quotationToDelete = state.quotations.find(
            q => q.id === quotationId,
          );
          if (quotationToDelete) {
            state.deletedQuotationBackup = quotationToDelete;
            state.quotations = state.quotations.filter(
              quotation => quotation.id !== quotationId,
            );
          }
        }
      })
      .addCase(deleteQuotation.fulfilled, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.success = action.payload?.message || "تم حذف عرض السعر بنجاح";
        state.error = null;
        // Quotation was already removed optimistically in pending
        state.deletedQuotationBackup = null;
      })
      .addCase(deleteQuotation.rejected, (state, action) => {
        // Don't set isLoading (wasn't set in pending)
        state.error = action.payload;
        state.success = null;
        // Revert optimistic deletion on error
        const quotationId = action.meta?.arg;
        const backup = state.deletedQuotationBackup;
        if (
          quotationId !== undefined &&
          backup &&
          Array.isArray(state.quotations)
        ) {
          const exists = state.quotations.find(q => q.id === quotationId);
          if (!exists) {
            state.quotations = [...state.quotations, backup];
            state.quotations.sort((a, b) => {
              if (a.id && b.id) return a.id - b.id;
              return 0;
            });
          }
        }
        state.deletedQuotationBackup = null;
      })
      // Delete Safe
      .addCase(deleteSafe.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSafe.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message || "تم حذف الخزنة بنجاح";
        state.error = null;
      })
      .addCase(deleteSafe.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearState,
  clearError,
  clearSuccess,
  clearReports,
  clearAccountDetails,
  clearJournalEntryDetails,
  clearExchangeRateDetails,
  clearCostCenterDetails,
  clearReceiptVoucherDetails,
  clearNoteReceivableDetails,
  clearNotePayableDetails,
  clearTaxDetails,
  clearProjectDetails,
  clearQuotationDetails,
  clearPaymentVoucherDetails,
  clearOpeningBalanceDetails,
  clearVendorDetails,
  clearCustomerDetails,
  clearUnitDetails,
  clearLabTestDetails,
  clearFinancialPeriodDetails,
  clearTrialBalance,
  clearAccountStatement,
  clearBankDetails,
  clearSafeDetails,
  logout,
  setProjects,
  setProjectsPagination,
  setCustomers,
  setCustomersPagination,
  setUnits,
  setUnitsPagination,
  setTaxes,
  setTaxesPagination,
  setCostCenters,
  setCostCentersPagination,
} = authSlice.actions;

export {
  // Positions
  getPositions,
  addPosition,
  // Job Titles
  getJobTitles,
  updateJobTitle,
  deleteJobTitle,
  getJobTitleDetails,
  // Departments
  getDepartments,
  addDepartment,
  updateDepartment,
  deleteDepartment,
  getDepartmentDetails,
  // Employees
  getEmployees,
  getEmployeeDetails,
  updateEmployee,
  addEmployee,
  deleteEmployee,
  toggleEmployeeStatus,
  // Work Shifts
  getWorkShifts,
  addWorkShift,
  updateWorkShift,
  deleteWorkShift,
  getWorkShiftDetails,
  // Leave Types
  getLeaveTypes,
  addLeaveType,
  updateLeaveType,
  deleteLeaveType,
  getLeaveTypeDetails,
  toggleLeaveTypeStatus,
  // Leave Requests
  getLeaveRequests,
  addLeaveRequest,
  updateLeaveRequest,
  deleteLeaveRequest,
  getLeaveRequestDetails,
  updateLeaveRequestStatus,
  // Attendance
  getAttendance,
  checkIn,
  checkOut,
  getAttendanceReports,
  // Salaries
  getSalaries,
  addSalary,
  getSalaryDetails,
  updateSalary,
  // Payslips
  getPayslips,
  getPayslipDetails,
  postPayslipJournal,
  getPayslipsSummary,
  getEmployeePayslipStatement,
  generatePayslip,
  // Leave Reports
  getLeaveReports,
  // Payroll Reports
  getPayrollReports,
  // Working Hours Reports
  getWorkingHoursReports,
  getLoansReports,
  getPenaltiesReports,
  // Accounts
  getAccountsTree,
  getPostingAccounts,
  getAccountLinks,
  getUnlinkedAccounts,
  updateAccountLink,
  getAccountDetails,
  toggleAccountStatus,
  addAccount,
  updateAccount,
  deleteAccount,
  // Cost Centers
  getCostCenters,
  getCostCentersTree,
  getCostCenterDetails,
  addCostCenter,
  updateCostCenter,
  toggleCostCenterStatus,
  deleteCostCenter,
  // Journal Entries
  getJournalEntries,
  getJournalEntryDetails,
  addJournalEntry,
  updateJournalEntry,
  acceptJournalEntry,
  postJournalEntry,
  deleteJournalEntry,
  reverseJournalEntry,
  // Receipt Vouchers
  getReceiptVouchers,
  getReceiptVoucherDetails,
  addReceiptVoucher,
  postReceiptVoucher,
  deleteReceiptVoucher,
  // Payment Vouchers
  getPaymentVouchers,
  getPaymentVoucherDetails,
  addPaymentVoucher,
  editPaymentVoucher,
  postPaymentVoucher,
  deletePaymentVoucher,
  // Opening Balances
  getOpeningBalances,
  addOpeningBalance,
  updateOpeningBalance,
  postOpeningBalance,
  deleteOpeningBalance,
  getOpeningBalanceDetails,
  // Currencies
  getCurrencies,
  getCurrencyDetails,
  addCurrency,
  updateCurrency,
  toggleCurrencyStatus,
  deleteCurrency,
  // Exchange Rates
  getExchangeRates,
  getExchangeRateDetails,
  getEffectiveExchangeRate,
  addExchangeRate,
  deleteExchangeRate,
  // Vendors
  getVendors,
  getVendorDetails,
  addVendor,
  updateVendor,
  toggleVendorStatus,
  deleteVendor,
  // Customers
  getCustomers,
  getCustomerDetails,
  addCustomer,
  updateCustomer,
  toggleCustomerStatus,
  deleteCustomer,
  // Financial Periods
  getFinancialPeriods,
  getFinancialPeriodDetails,
  addFinancialPeriod,
  updateFinancialPeriod,
  closeFinancialPeriod,
  deleteFinancialPeriod,
  // Trial Balance
  getTrialBalance,
  // Account Statement
  getAccountStatement,
  // Banks
  getBanks,
  getBankDetails,
  addBank,
  updateBank,
  toggleBankStatus,
  deleteBank,
  // Safes
  getSafes,
  getSafeDetails,
  addSafe,
  updateSafe,
  toggleSafeStatus,
  deleteSafe,
  // Banks and Safes
  getBanksAndSafes,
  // Notes Receivable
  getNotesReceivable,
  addNoteReceivable,
  getNoteReceivableDetails,
  collectNoteReceivable,
  cancelNoteReceivable,
  updateNoteReceivable,
  // Notes Payable
  getNotesPayable,
  addNotePayable,
  getNotePayableDetails,
  payNotePayable,
  cancelNotePayable,
  updateNotePayable,
  // Taxes
  getTaxes,
  addTax,
  updateTax,
  getTaxDetails,
  toggleTaxStatus,
  deleteTax,
  // Projects
  getProjects,
  addProject,
  updateProject,
  getProjectDetails,
  toggleProjectStatus,
  deleteProject,
  // Quotations
  getQuotations,
  addQuotation,
  updateQuotation,
  getQuotationDetails,
  sendQuotationToClient,
  approveQuotationItems,
  deleteQuotation,
  // Units
  getUnits,
  getUnitDetails,
  addUnit,
  updateUnit,
  deleteUnits,
  toggleUnitStatus,
  getMeasurables,
  // Lab Tests
  getLabTests,
  getLabTestsDetails,
  addLabTest,
  updateLabTest,
  deleteLabTests,
  toggleLabTestStatus,
};

export default authSlice.reducer;
